import React, { useMemo, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import ListIcon from "@mui/icons-material/List";
import { getListBrowserStore } from "@ts-private/list-browser";
import { useApolloFetching } from "@ts-private/list-browser-apollo-extensions";
import { Card, useToast } from "@totalsoft/rocket-ui";
import HierarchyListItem from "./HierarchyListItem";
import { HIERARCHIES_QUERY } from "../queries";
import { useListBrowserFunctions } from "hooks/useListBrowserFunctions";
import HeaderPortal from "components/layout/HeaderPortal";
import TableListBrowser from "components/common/table-list-browser/TableListBrowser";
import HeaderLinks from "./HeaderLinks";
import HierarchiesListFilters from "./HierarchyListFilters";
import { hasAuthorization } from "utils/functions";
import * as rightsCodes from "constants/rightsCodes";
import { RightsContext } from "providers/RightsContext";
import { useMutation } from "@apollo/client";
import { REMOVE_PARTNER_HIERARCHY } from "../mutations";
import  useConfirmDialog  from "features/common/components/confirm/useConfirmDialog";
import { useTranslation } from "react-i18next";
import { useSimpleError } from "hooks/apolloWrappers";

const hierarchiesStore = getListBrowserStore({
  name: "hierarchies-browser",
  filterDefinitions: [
    {
      name: "hierarchyName",
      caption: "PartnerHierarchy.HierarchyName",
      type: "text"
    },
    {
      name: "hierarchyCode",
      caption: "PartnerHierarchy.HierarchyCode",
      type: "text"
    },
    {
      name: "isActive",
      caption: "PartnerHierarchy.IsActive",
      type: "boolean",
      initialValue: null
    },
    {
      name: "partnerId",
      caption: "PartnerHierarchy.Partner",
      type: "select",
      underlyingType: "number"
    }
  ],
  orderByFields: [
    {
      caption: "PartnerHierarchy.HierarchyName",
      value: "hierarchyName"
    },
    {
      caption: "PartnerHierarchy.HierarchyCode",
      value: "hierarchyCode"
    }
  ],
  pager: {
    orderBy: "HierarchyName",
    direction: "asc"
  }
});

const HierarchiesListBrowser = () => {
  const [t] = useTranslation();
  const showError = useSimpleError();
  const  confirm  = useConfirmDialog();
  const history = useHistory();
  const { textResolver } = useListBrowserFunctions();
  const rights = useContext(RightsContext);
  const canWrite = hasAuthorization(rightsCodes.MASTER_PARTNER_HIERARCHIES_WRITE, rights);
  const showToast = useToast();

  const getHierarchiesFetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: HIERARCHIES_QUERY,
        name: "partnerHierarchies"
      }
    }),
    []
  );

  useApolloFetching(hierarchiesStore, getHierarchiesFetchingOptions);

  const columns = useMemo(
    () => [
      { name: "hierarchyName", caption: "PartnerHierarchy.HierarchyName" },
      { name: "hierarchyCode", caption: "PartnerHierarchy.HierarchyCode" },
      { name: "isActive", caption: "PartnerHierarchy.IsActive", width: 10, align: "center" },
      { name: "actions", caption: "Actions", width: 15 }
    ],
    []
  );

  const handleAdd =  useCallback(() => {
    history.push(`/partnerHierarchies/new`);
  }, [history]);

  const handleEdit = useCallback((partnerHierarchyId) => {
    history.push(`/partnerHierarchies/${partnerHierarchyId}`);
  }, [history]);

  const [deleteMethod] = useMutation(REMOVE_PARTNER_HIERARCHY, {
    onError: error => {
      showError(error);
    },
    update: cache => {
      showToast(t("PartnerHierarchy.PartnerHierarchyRemoved"), "success", { autoClose: 3000 });
      cache.evict({ id: "ROOT_QUERY", fieldName: "partnerHierarchies" });
      cache.gc();
    }
  });

  const handleDelete = useCallback((partnerHierarchyId) => {
    confirm(
      () => {
        deleteMethod({ variables: { partnerHierarchyId: partnerHierarchyId } });
      },
      {
        title: t('ListBrowser.DeleteDialogTitle'),
        content: t('ListBrowser.AreYouSureDelete')
      }
    );
  }, [deleteMethod, t, confirm]);

  const filters = useCallback(() => <HierarchiesListFilters store={hierarchiesStore}/>, [])
  const itemComponent = 
          useCallback(({ data }) => 
                    <HierarchyListItem data={data} canWrite={canWrite} onEdit={handleEdit} onDelete={handleDelete} />, 
            [canWrite, handleEdit, handleDelete]);
  return (
    <>
      <HeaderPortal inEdit={false}>
        <HeaderLinks onAdd={handleAdd}></HeaderLinks>
      </HeaderPortal>
      <Card icon={ListIcon} iconColor="primary">
        <TableListBrowser
          name="hierarchies-browser"
          store={hierarchiesStore}
          ItemComponent={itemComponent}
          FiltersComponent={filters}
          textResolver={textResolver}
          columns={columns}
        />
      </Card>
    </>
  );
};

export default HierarchiesListBrowser;
